import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

/**
 * One tile on a landing page: a photo, a bold name, one line, an arrow in the shop's color.
 * Used by the home grid and by every group landing (Shirts, Signs) before the designer opens.
 */
export function GroupCard({ to, image, title, hook, badge, theme, testid, contain, tintHex, idPrefix = "group-card" }) {
    const url = image ? mediaUrl(image) : null;
    return (
        <Link to={to} data-testid={testid} className="group flex flex-col border-2 border-zinc-900 bg-white transition-transform duration-200 hover:-translate-y-1">
            <div className={`relative aspect-[4/3] w-full overflow-hidden border-b-2 border-zinc-900 ${contain ? "bg-zinc-100" : "bg-zinc-200"}`}>
                {url ? (
                    <img src={url} alt="" loading="lazy" data-testid={`${idPrefix}-image`}
                        className={`absolute inset-0 h-full w-full transition-transform duration-500 group-hover:scale-105 ${contain ? "object-contain p-4" : "object-cover"}`} />
                ) : (
                    <div className="flex h-full w-full items-center justify-center font-display text-xl font-black uppercase text-zinc-400">{title}</div>
                )}
                {tintHex && url && (
                    <span className="pointer-events-none absolute inset-0 p-4 transition-transform duration-500 group-hover:scale-105" style={{ mixBlendMode: "multiply" }}>
                        <span className="block h-full w-full" style={{ backgroundColor: tintHex, maskImage: `url(${url})`, WebkitMaskImage: `url(${url})`, maskSize: "contain", WebkitMaskSize: "contain", maskPosition: "center", WebkitMaskPosition: "center", maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat" }} />
                    </span>
                )}
                {badge && (
                    <span className="absolute left-0 top-0 bg-[#1e6b34] px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white" data-testid={`${idPrefix}-badge`}>{badge}</span>
                )}
            </div>
            <div className="flex flex-1 items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                    <h3 className="font-display text-base font-black uppercase leading-tight tracking-tight text-zinc-900" data-testid={`${idPrefix}-title`}>{title}</h3>
                    {hook && <p className="mt-0.5 text-sm text-zinc-600" data-testid={`${idPrefix}-hook`}>{hook}</p>}
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center transition-transform duration-200 group-hover:translate-x-1" style={{ background: theme?.primary || "#0b0b0b", color: theme?.on || "#fff" }}>
                    <ArrowRight size={16} />
                </span>
            </div>
        </Link>
    );
}

/** The heading every landing page opens with: a mono kicker, a bold uppercase title, one plain line. */
export function LandingHead({ kicker, title, line, right, testid = "landing-head" }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-4" data-testid={testid}>
            <div className="min-w-0">
                {kicker && <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500" data-testid={`${testid}-kicker`}>{kicker}</p>}
                <h1 className="mt-1 font-display text-3xl font-black uppercase leading-none tracking-tight text-zinc-900 sm:text-4xl" data-testid={`${testid}-title`}>{title}</h1>
                {line && <p className="mt-2 max-w-2xl text-base text-zinc-600" data-testid={`${testid}-line`}>{line}</p>}
            </div>
            {right}
        </div>
    );
}
